import { useRef, useState, useEffect } from 'react';
import { useChatJourney } from './useChatJourney';
import { useChatSimulator } from './useChatSimulator';
import { useNodeTree } from './useNodeTree';
import { useResponsiveLayout } from '../../../../utils/hooks/useResponsiveLayout';

const useChatJourneyTwoPanel = () => {
  const toast = useRef(null);
  const menuRefs = useRef({});
  const [isLoading] = useState(false);
  const [showSimulator, setShowSimulator] = useState(false);
  const { isMobile } = useResponsiveLayout();
  const [selectedDraft, setSelectedDraft] = useState(null);

  const {
    nodes,
    setNodes,
    isEnabled,
    editHistory,
    selectedVersion,
    setSelectedVersion,
    hasChanges,
    showInstructions,
    setShowInstructions,
    saveChatJourney,
    loadVersion,
    visibleHistory,
    totalRecords,
    onLazyLoad,
    deleteDraft,
    handleToggleChange: handleToggleChangeFromHook
  } = useChatJourney(toast);

  const {
    chatHistory,
    setChatHistory,
    currentNode,
    parentNode,
    handleUserChoice,
    handleBack
  } = useChatSimulator(nodes);

  const {
    selectedNode,
    setSelectedNode,
    showDialog,
    setShowDialog,
    setDialogType,
    question,
    setQuestion,
    response,
    setResponse,
    expandedKeys,
    setExpandedKeys,
    pathNodes,
    setPathNodes,
    handleSaveNode,
    updateNode,
    addChildNode,
    removeNode,
    findParent
  } = useNodeTree(toast, nodes);

  const [questionCount, setQuestionCount] = useState(0);
  const [responseCount, setResponseCount] = useState(0);

  useEffect(() => {
    const questionText = question ? question.replace(/<[^>]*>/g, '') : '';
    setQuestionCount(questionText.length);
  }, [question]);

  useEffect(() => {
    const responseText = response ? response.replace(/<[^>]*>/g, '') : '';
    setResponseCount(responseText.length);
  }, [response]);


  const handleEditorChange = (e, isQuestion) => {
    isQuestion ? setQuestion(e.htmlValue) : setResponse(e.htmlValue);
  };

  const handleToggleChange = async (e) => {
    await handleToggleChangeFromHook(e.value);
  };

  const handleAddFirstNode = () => {
    setDialogType('add');
    setSelectedNode(null);
    setQuestion('');
    setResponse('');
    setShowDialog(1);
  };

  const handleDraftSelect = (draft) => {
    if (selectedDraft?.id === draft.id) {
      setSelectedDraft(null);
      if (hasChanges) return;
      const publishedVersion = editHistory.find(item => item.type === 'published' && item.isEnabled);
      if (publishedVersion) {
        loadVersion(publishedVersion);
      } else {
        setNodes([]);
        setSelectedVersion(null);
      }
    } else {
      const currentState = { nodes: [...nodes], isEnabled, hasChanges };
      setSelectedDraft(draft);
      draft.isSelected = true;
      loadVersion(draft);
      draft.previousState = currentState;
      const getAllNodeKeys = (nodes) => {
        const keys = {};
        const traverse = (node) => {
          keys[node.key] = true;
          if (node.children) node.children.forEach(traverse);
        };
        nodes.forEach(traverse);
        return keys;
      };
      setExpandedKeys(getAllNodeKeys(draft.nodes));
    }
  };

  const currentDraft = editHistory.find(item => item.type === 'draft');

  return {
    toast,
    isEnabled,
    handleToggleChange,
    showSimulator,
    setShowSimulator,
    saveChatJourney,
    isMobile,
    chatHistory,
    setChatHistory,
    currentNode,
    parentNode,
    handleUserChoice,
    handleBack,
    currentDraft,
    selectedDraft,
    handleDraftSelect,
    deleteDraft,
    setSelectedDraft,
    nodes,
    setNodes,
    selectedNode,
    setSelectedNode,
    expandedKeys,
    setExpandedKeys,
    pathNodes,
    setPathNodes,
    showDialog,
    setShowDialog,
    setDialogType,
    setQuestion,
    setResponse,
    updateNode,
    addChildNode,
    removeNode,
    findParent,
    menuRefs,
    handleAddFirstNode,
    hasChanges,
    showInstructions,
    setShowInstructions,
    editHistory,
    selectedVersion,
    loadVersion,
    visibleHistory,
    totalRecords,
    onLazyLoad,
    isLoading,
    question,
    setQuestionCount,
    questionCount,
    response,
    setResponseCount,
    responseCount,
    handleEditorChange,
    handleSaveNode
  };
};

export default useChatJourneyTwoPanel;
